import type { MapData } from '@shared/types/map';
import { loadMap1Raw, getMap1Bordered } from '../boot/mapData';

/**
 * Configuración de un fondo de mapa para las pantallas de Auth.
 */
export interface AuthBackgroundDef {
  /** ID del mapa a cargar */
  mapId: number;
  /** Coordenada X central (en tiles) */
  centerX: number;
  /** Coordenada Y central (en tiles) */
  centerY: number;
}

/**
 * Tipos de pantalla que usan fondos de mapa.
 */
export type AuthScreenType = 
  | 'login'           // Pantalla de login/registro
  | 'character_list'  // Listado de personajes
  | 'character_create'; // Creación de personaje

/**
 * Configuración por defecto de fondos para cada pantalla.
 * Cuando tengamos más mapas, cada pantalla puede usar un mapa diferente.
 */
export const AUTH_BACKGROUND_DEFAULTS: Record<AuthScreenType, AuthBackgroundDef> = {
  // Centro real de la explanada/cruce de la ciudad de debug (tile 614,612), medido
  // en pantalla: a 614 el camino vertical del cruce cae justo en el centro (con 613
  // quedaba ~1 tile corrido a la derecha → faltaba 1 tile a la izquierda).
  login: {
    mapId: 1,
    centerX: 614,
    centerY: 612,
  },
  character_list: {
    mapId: 1,
    centerX: 614,
    centerY: 612,
  },
  character_create: {
    mapId: 1,
    centerX: 614,
    centerY: 612,
  },
};

/**
 * Pool de fondos posibles para la pantalla de login.
 * Al entrar al login se elige uno al azar de esta lista.
 * Agregar acá nuevas posiciones/mapas lindos a medida que existan.
 */
export const LOGIN_BACKGROUND_POOL: AuthBackgroundDef[] = [
  // Centrado en la explanada (614,612). La variante anterior en 617 corría la vista
  // ~5 tiles a la derecha y dejaba la plaza descentrada. Agregar nuevas posiciones
  // SOLO si quedan bien encuadradas (probarlas en pantalla antes).
  { mapId: 1, centerX: 614, centerY: 612 },
];

/**
 * Devuelve un fondo aleatorio del pool de login.
 * Cae al default de login si el pool estuviera vacío.
 */
export function getRandomLoginBackground(): AuthBackgroundDef {
  if (LOGIN_BACKGROUND_POOL.length === 0) {
    return AUTH_BACKGROUND_DEFAULTS.login;
  }
  const idx = Math.floor(Math.random() * LOGIN_BACKGROUND_POOL.length);
  return LOGIN_BACKGROUND_POOL[idx];
}

/**
 * Cache de MapData cargados para evitar recargar el mismo mapa.
 */
const mapDataCache: Map<number, MapData> = new Map();

/**
 * Obtiene el MapData para un mapa específico.
 * Por ahora solo tenemos map1, pero esto está preparado para más mapas.
 *
 * Es async porque map1.json se fetchea en runtime (ya no se bundlea). `loadMap1Raw()` está
 * memoizado, así que al boot suele resolver instantáneo si el mapa ya se descargó.
 */
export async function getMapData(mapId: number): Promise<MapData> {
  // Verificar cache primero
  if (mapDataCache.has(mapId)) {
    return mapDataCache.get(mapId)!;
  }

  // Por ahora solo tenemos map1
  // Cuando tengamos más mapas, agregaremos más casos aquí
  let mapData: MapData;

  switch (mapId) {
    case 1:
    default:
      // map1: usar el bordered memoizado y SIN deep-clone (compartido con el warmup del juego).
      // Evita el JSON.parse(JSON.stringify) de 35MB que congelaba el login. Ver boot/mapData.ts.
      await loadMap1Raw();
      mapData = getMap1Bordered();
      break;
    // Ejemplo para futuros mapas:
    // case 2:
    //   mapData = loadMapWithBorders(await loadMap2Raw());
    //   break;
  }

  mapDataCache.set(mapId, mapData);

  return mapData;
}

/**
 * Obtiene la configuración de fondo para una pantalla específica.
 */
export function getBackgroundForScreen(screen: AuthScreenType): AuthBackgroundDef {
  return AUTH_BACKGROUND_DEFAULTS[screen];
}

/**
 * Crea la configuración de fondo a partir de la posición de un personaje.
 * Útil para mostrar el fondo del mapa donde está el personaje.
 */
export function getBackgroundForCharacter(
  mapId: number | undefined,
  x: number | undefined,
  y: number | undefined
): AuthBackgroundDef {
  // Si no tiene datos, usar el default de character_list
  if (mapId === undefined || x === undefined || y === undefined) {
    return AUTH_BACKGROUND_DEFAULTS.character_list;
  }

  return {
    mapId,
    centerX: x,
    centerY: y,
  };
}

















